/**
 * Transition utilities for property interpolation.
 *
 * Easing functions follow CSS transition-timing-function semantics.
 * Color interpolation works in linear RGB (component-wise).
 */

import type { EasingFunction } from '../../scenedata/behaviors/propertiesdata';
import type { PropertyValue } from '../../behaviors/tracebehavior';

// ── Easing ──

/**
 * Easing function: maps normalized time [0,1] → eased progress.
 * `back` overshoots outside [0,1] mid-flight and `anticipate` undershoots at
 * the start, by design — consumers interpolating bounded values (color,
 * alpha) clamp on application; transforms extrapolate through the overshoot.
 */
type EasingFn = (t: number) => number;

/**
 * The named-easing alias table — THE cross-client easing contract.
 *
 * Every easing IS a cubic bezier: the named curves are aliases for the
 * control points below, evaluated by the same solver as authored
 * `cubic-bezier(...)` strings. A conforming client implements one bezier
 * evaluator plus this table — no per-curve formulas — and any easing rides
 * KHR's `pointer/interpolate` (p1/p2) unchanged.
 *
 * Motion-design intent (2026-07 passes, reviewed with design; bezier-
 * canonical since the KHR alignment):
 *   easeIn = departure (cubic commit) · easeOut = the workhorse arrival
 *   easeInOut = asymmetric standard (CSS/Material) · snap = response
 *   back = confidence (one ≈5.7% pass, no echo) · anticipate = wind-up
 * Curves a single bezier arc cannot express (bounce, ringing springs)
 * are not easings — author them as animation-driven transitions.
 *
 * `easeIn` and `back` are exact: both shapes are cubic polynomials, and a
 * cubic polynomial is exactly one bezier (back's y1 is 17/12 — the tuned
 * 1.25 overshoot coefficient in Bernstein form).
 */
export const EASING_CONTROL_POINTS: Record<string, readonly [number, number, number, number]> = {
    linear: [0, 0, 1, 1],
    easeIn: [1 / 3, 0, 2 / 3, 0],
    easeOut: [0.22, 1, 0.36, 1],
    easeInOut: [0.4, 0, 0.2, 1],
    snap: [0.05, 0.7, 0.1, 1],
    back: [1 / 3, 17 / 12, 2 / 3, 1],
    anticipate: [0.35, -0.25, 0.15, 1],
};

const EASING_FUNCTIONS: Record<string, EasingFn> = Object.fromEntries([
    // linear skips the solver — identity is exact and the lookup runs per frame
    ['linear', (t: number) => t <= 0 ? 0 : t >= 1 ? 1 : t] as const,
    ...Object.entries(EASING_CONTROL_POINTS)
        .filter(([name]) => name !== 'linear')
        .map(([name, [x1, y1, x2, y2]]) =>
            [name, parseCubicBezier(`cubic-bezier(${x1}, ${y1}, ${x2}, ${y2})`)!] as const),
]);

/** Parsed cubic-bezier functions, cached — the lookup runs every frame. */
const _bezierCache = new Map<string, EasingFn>();

/**
 * Parse the four control points out of a CSS `cubic-bezier(x1, y1, x2, y2)`
 * spec. Control-point x values must be in [0,1] (CSS rule — keeps x(t)
 * monotonic so it's solvable); y values are unrestricted, which is how
 * customs overshoot. Returns null for malformed/out-of-range specs.
 */
function parseCubicBezierControlPoints(spec: string): [number, number, number, number] | null {
    const m = /^cubic-bezier\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)$/.exec(spec);
    if (!m) return null;
    const x1 = parseFloat(m[1]), y1 = parseFloat(m[2]), x2 = parseFloat(m[3]), y2 = parseFloat(m[4]);
    if ([x1, y1, x2, y2].some(Number.isNaN) || x1 < 0 || x1 > 1 || x2 < 0 || x2 > 1) return null;
    return [x1, y1, x2, y2];
}

/**
 * Resolve any easing spec to its cubic-bezier control points
 * `[x1, y1, x2, y2]` — named easings via the EASING_CONTROL_POINTS alias
 * table, `cubic-bezier(...)` strings by parsing; unknown/missing/invalid
 * specs fall back to easeInOut's points (the same degrade path as
 * getEasingFunction). This is the edit-time compile step for KHR
 * `pointer/interpolate` p1/p2 inputs: `{duration, easing}` never reaches
 * GraphData — only duration + control points do.
 */
export function easingToControlPoints(easing: EasingFunction | undefined): [number, number, number, number] {
    if (easing) {
        const named = EASING_CONTROL_POINTS[easing];
        if (named) return [named[0], named[1], named[2], named[3]];
        if (easing.startsWith('cubic-bezier(')) {
            const parsed = parseCubicBezierControlPoints(easing);
            if (parsed) return parsed;
        }
    }
    const d = EASING_CONTROL_POINTS.easeInOut;
    return [d[0], d[1], d[2], d[3]];
}

/** Build an easing function from a parsed `cubic-bezier(...)` spec. */
function parseCubicBezier(spec: string): EasingFn | null {
    const points = parseCubicBezierControlPoints(spec);
    if (!points) return null;
    const [x1, y1, x2, y2] = points;

    const bez = (t: number, p1: number, p2: number) =>
        3 * (1 - t) * (1 - t) * t * p1 + 3 * (1 - t) * t * t * p2 + t * t * t;

    return (u: number) => {
        if (u <= 0) return 0;
        if (u >= 1) return 1;
        // Solve x(t) = u by bisection — x is monotonic for x1, x2 in [0,1]
        let lo = 0, hi = 1;
        for (let i = 0; i < 24; i++) {
            const mid = (lo + hi) / 2;
            if (bez(mid, x1, x2) < u) lo = mid; else hi = mid;
        }
        return bez((lo + hi) / 2, y1, y2);
    };
}

export function getEasingFunction(name: EasingFunction | undefined): EasingFn {
    if (!name) return EASING_FUNCTIONS.easeInOut;
    const named = EASING_FUNCTIONS[name];
    if (named) return named;
    if (name.startsWith('cubic-bezier(')) {
        let fn = _bezierCache.get(name);
        if (!fn) {
            fn = parseCubicBezier(name) ?? EASING_FUNCTIONS.easeInOut;
            _bezierCache.set(name, fn);
        }
        return fn;
    }
    // Unknown easing in loaded data — degrade gracefully, never crash.
    return EASING_FUNCTIONS.easeInOut;
}

// ── Color parsing ──

/** Parse "#RRGGBB" or "#RRGGBBAA" hex string to [r, g, b] in 0–1 range */
export function parseHexColor(hex: string): [number, number, number] | null {
    if (!hex.startsWith('#')) return null;
    const h = hex.slice(1);
    if (h.length !== 6 && h.length !== 8) return null;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return [r / 255, g / 255, b / 255];
}

/** Encode [r, g, b] (0–1 range) back to "#RRGGBB". Alpha channel is not preserved on round-trip. */
export function toHexColor(rgb: [number, number, number]): string {
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
    const r = clamp(rgb[0]).toString(16).padStart(2, '0');
    const g = clamp(rgb[1]).toString(16).padStart(2, '0');
    const b = clamp(rgb[2]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`.toUpperCase();
}

// ── Driver progress ──

/**
 * Compute interpolation progress from a property-driven transition's driver value.
 * Maps the driver value through the range and applies easing.
 *
 * @param driverValue - Current value of the driver property
 * @param range - Maps [start, end] of driver value to [0, 1] progress. Inverted ranges are valid.
 * @param easing - Easing applied after range mapping
 * @returns Eased progress clamped to [0, 1]
 */
export function computeDriverProgress(
    driverValue: number,
    range: [number, number] = [0, 1],
    easing?: EasingFunction,
): number {
    if (Number.isNaN(driverValue)) return 1; // driver unavailable → full state
    const [start, end] = range;
    const span = end - start;
    if (span === 0) return 1; // degenerate range → full state
    const raw = (driverValue - start) / span;
    const clamped = Math.max(0, Math.min(1, raw));
    return getEasingFunction(easing ?? 'linear')(clamped);
}

// ── Interpolation ──

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

/**
 * Interpolate between two property values at a given progress (0–1, already eased).
 * Returns the interpolated value, or `to` if the types are not interpolable.
 */
export function interpolateValue(
    from: PropertyValue,
    to: PropertyValue,
    progress: number,
): PropertyValue {
    // Number
    if (typeof from === 'number' && typeof to === 'number') {
        return lerp(from, to, progress);
    }

    // Color (hex strings)
    if (typeof from === 'string' && typeof to === 'string') {
        const fromRgb = parseHexColor(from);
        const toRgb = parseHexColor(to);
        if (fromRgb && toRgb) {
            return toHexColor([
                lerp(fromRgb[0], toRgb[0], progress),
                lerp(fromRgb[1], toRgb[1], progress),
                lerp(fromRgb[2], toRgb[2], progress),
            ]);
        }
        // Non-color strings are discrete — snap at the midpoint of eased
        // progress (CSS `allow-discrete` timing; CANOPY_STATES §6.2 rule 2).
        return progress < 0.5 ? from : to;
    }

    // vec3 / vec4 tuples
    if (Array.isArray(from) && Array.isArray(to) && from.length === to.length) {
        if (from.length === 3) {
            return [
                lerp(from[0], to[0], progress),
                lerp(from[1], to[1], progress),
                lerp(from[2], to[2], progress),
            ] as PropertyValue;
        }
        if (from.length === 4) {
            const f = from;
            const t = to as [number, number, number, number];
            return [
                lerp(f[0], t[0], progress),
                lerp(f[1], t[1], progress),
                lerp(f[2], t[2], progress),
                lerp(f[3], t[3], progress),
            ] as PropertyValue;
        }
    }

    // Boolean / non-interpolable — snap at midpoint
    return progress < 0.5 ? from : to;
}
